import { useState, useEffect, useMemo } from 'react';
import imageCompression from 'browser-image-compression';
import { Edit, Plus, Trash2, Search as SearchIcon } from 'lucide-react';
import {
  getProperties, createProperty, updateProperty, deleteProperty,
  getAreas, getCurrencyRate, uploadImage, getCloudinaryStatus, uploadToCloudinary,
  getCloudinarySignature,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import PropertyFormModal from './PropertyFormModal';
import { emptyForm } from './propertyFormConstants';

const PropertiesManagement = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [locations, setLocations] = useState([]);
  const [currencyRate, setCurrencyRate] = useState(15500);
  const [cloudinaryEnabled, setCloudinaryEnabled] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [search]);


  const [form, setForm] = useState(emptyForm);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const res = await getProperties({});
      setProperties(res.data);
    } catch (err) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const res = await getAreas(true); // Only active areas
      setLocations(res.data.map(a => a.name));
    } catch (err) {
      // Fallback to default locations if API fails
      setLocations(['Canggu', 'Berawa', 'Pererenan', 'Seminyak', 'Uluwatu', 'Bingin', 'Sanur', 'Ubud']);
    }
  };

  const fetchCurrencyRate = async () => {
    try {
      const res = await getCurrencyRate();
      setCurrencyRate(res.data.usd_to_idr);
    } catch (err) {
      // Currency rate fetch failed - using default rate
    }
  };

  const checkCloudinaryStatus = async () => {
    try {
      const res = await getCloudinaryStatus();
      setCloudinaryEnabled(res.data.configured);
    } catch (err) {
      setCloudinaryEnabled(false);
    }
  };

  useEffect(() => {
    fetchProperties();
    fetchLocations();
    fetchCurrencyRate();
    checkCloudinaryStatus();
  }, []);

  // Per-file progress array: each entry is { name, status: 'pending'|'compressing'|'uploading'|'success'|'error', error? }
  const [uploadQueue, setUploadQueue] = useState([]);
  // True while iOS materializes files from the Files picker (no `onChange`
  // event yet). Lets us show a "Preparing files…" indicator immediately on tap
  // instead of an apparent freeze.
  const [preparing, setPreparing] = useState(false);

  const handleImageUpload = async (e) => {
    const inputEl = e.target;
    const files = Array.from(inputEl.files || []);
    setPreparing(false);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    // Seed the per-file queue
    const initial = files.map((f) => ({ name: f.name, status: 'pending' }));
    setUploadQueue(initial);

    const successUrls = [];
    const failures = [];
    let completed = 0;

    // Concurrency-limited parallel upload. Files are pre-compressed to <400KB
    // so we can comfortably parallelise more than the previous limit of 5
    // without tripping Cloudinary's burst throttling.
    const CONCURRENCY = 6;

    // Fetch ONE Cloudinary signature for the whole batch (valid ~1h, reusable
    // for multiple uploads to the same folder). Saves N round-trips.
    let cachedSig = null;
    if (cloudinaryEnabled) {
      try {
        const { data } = await getCloudinarySignature('image', 'properties');
        cachedSig = data;
      } catch (sigErr) {
        if (process.env.NODE_ENV !== 'production') console.error('Signature prefetch failed; falling back to per-file:', sigErr);
      }
    }

    // Compress: max 2400px long-edge, ~85% JPEG quality. Typical iPhone photo
    // shrinks from 5-6MB to ~300-500KB — a 10-15× upload speedup with no
    // visible loss for property listings.
    const COMPRESSION_OPTS = {
      maxSizeMB: 0.6,
      maxWidthOrHeight: 2400,
      useWebWorker: true,
      fileType: 'image/jpeg',
      initialQuality: 0.85,
    };

    const prepareFile = async (file) => {
      // Skip compression for tiny files (<400KB) — likely already optimized.
      if (file.size <= 400 * 1024) return file;
      // Skip non-image MIME types defensively.
      if (!file.type.startsWith('image/')) return file;
      try {
        return await imageCompression(file, COMPRESSION_OPTS);
      } catch (compErr) {
        if (process.env.NODE_ENV !== 'production') console.warn('Compression skipped:', file.name, compErr);
        return file; // fall back to original on any compression error
      }
    };

    const uploadOne = async (file, idx) => {
      setUploadQueue((q) => q.map((item, i) => i === idx ? { ...item, status: 'compressing' } : item));
      try {
        const prepared = await prepareFile(file);
        setUploadQueue((q) => q.map((item, i) => i === idx ? { ...item, status: 'uploading' } : item));
        let url;
        if (cloudinaryEnabled) {
          const result = await uploadToCloudinary(prepared, 'properties', cachedSig);
          if (result && result.secure_url) {
            url = result.secure_url;
          } else {
            throw new Error(result?.error?.message || 'Cloudinary upload failed');
          }
        } else {
          const result = await uploadImage(prepared);
          url = `${process.env.REACT_APP_BACKEND_URL}${result.data.url}`;
        }
        successUrls.push(url);
        setUploadQueue((q) => q.map((item, i) => i === idx ? { ...item, status: 'success' } : item));
      } catch (perFileErr) {
        if (process.env.NODE_ENV !== 'production') console.error('Single file upload failed:', perFileErr);
        const errMsg = perFileErr.response?.data?.detail || perFileErr.message || 'Upload error';
        failures.push({ file: file.name, error: errMsg });
        setUploadQueue((q) => q.map((item, i) => i === idx ? { ...item, status: 'error', error: errMsg } : item));
      } finally {
        completed += 1;
        setUploadProgress(Math.round((completed / files.length) * 100));
      }
    };

    // Process the files in batches of CONCURRENCY
    try {
      const tasks = files.map((file, idx) => () => uploadOne(file, idx));
      for (let i = 0; i < tasks.length; i += CONCURRENCY) {
        await Promise.all(tasks.slice(i, i + CONCURRENCY).map((fn) => fn()));
      }

      setUploadProgress(100);

      if (successUrls.length > 0) {
        setForm(prev => ({
          ...prev,
          images: [...prev.images, ...successUrls],
          cover_image: prev.cover_image || successUrls[0],
        }));
        toast.success(`${successUrls.length} of ${files.length} image(s) uploaded${cloudinaryEnabled ? ' to cloud' : ''}`);
      }

      if (failures.length > 0) {
        const firstFail = failures[0];
        toast.error(`${failures.length} upload(s) failed: ${firstFail.file} — ${firstFail.error}`);
      }
    } finally {
      // Brief delay so the user can see the final state of the per-file list
      setTimeout(() => {
        setUploading(false);
        setUploadProgress(0);
        setUploadQueue([]);
      }, 800);
      // Reset input so user can re-pick the same file(s) if needed
      if (inputEl) inputEl.value = '';
    }
  };

  const removeImage = (index) => {
    setForm(prev => {
      const newImages = prev.images.filter((_, i) => i !== index);
      return {
        ...prev,
        images: newImages,
        cover_image: prev.cover_image === prev.images[index] ? newImages[0] || '' : prev.cover_image,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // The backend converts IDR → USD on persistence and stores both `price`
    // (USD) and `price_idr` (anchor). The frontend just forwards the raw
    // amount + the currency flag; we never pre-convert here to avoid double
    // conversion when the backend also does it.
    const parsePrice = (rawValue) => {
      if (!rawValue) return null;
      const n = parseFloat(rawValue);
      return !n || isNaN(n) ? null : Math.round(n);
    };

    const saleAmt = parsePrice(form.price);
    const nightlyAmt = parsePrice(form.rental_price);
    const monthlyAmt = parsePrice(form.monthly_rental_price);
    const yearlyAmt = parsePrice(form.yearly_rental_price);

    const monthlyCurrency = form.monthly_rental_price_currency || 'IDR';
    const yearlyCurrency = form.yearly_rental_price_currency || 'IDR';

    // For each price, send raw amount + IDR anchor when currency is IDR. The
    // backend's create/update handlers convert `price` to USD when
    // `price_currency === 'IDR'` and persist the anchor as-is in *_idr fields.
    const data = {
      ...form,
      land_size: form.land_size ? parseInt(form.land_size) : null,
      building_size: form.building_size ? parseInt(form.building_size) : null,
      // Sale price (kept required by PropertyCreate)
      price: saleAmt ?? 0,
      price_currency: form.price_currency,
      price_idr: form.price_currency === 'IDR' ? saleAmt : null,
      // Nightly
      rental_price: nightlyAmt,
      rental_price_currency: form.rental_price_currency,
      rental_price_idr: form.rental_price_currency === 'IDR' ? nightlyAmt : null,
      // Monthly
      monthly_rental_price: monthlyAmt,
      monthly_rental_price_idr: monthlyCurrency === 'IDR' ? monthlyAmt : null,
      // Yearly
      yearly_rental_price: yearlyAmt,
      yearly_rental_price_idr: yearlyCurrency === 'IDR' ? yearlyAmt : null,
      // Short-term rental fields
      max_guests: form.max_guests ? parseInt(form.max_guests) : null,
      min_nights: form.min_nights ? parseInt(form.min_nights) : 1,
      max_nights: form.max_nights ? parseInt(form.max_nights) : null,
      cleaning_fee: form.cleaning_fee ? parseInt(form.cleaning_fee) : null,
      check_in_time: form.check_in_time || '15:00',
      check_out_time: form.check_out_time || '11:00',
      // Leasehold fields
      lease_years_remaining: form.lease_years_remaining ? parseInt(form.lease_years_remaining) : null,
      lease_expiry_year: form.lease_expiry_year ? parseInt(form.lease_expiry_year) : null,
      lease_extendable: form.lease_extendable || null,
      latitude: form.latitude ? parseFloat(form.latitude) : null,
      longitude: form.longitude ? parseFloat(form.longitude) : null,
      ownership_type: form.ownership_type === 'none' ? null : form.ownership_type || null,
    };

    try {
      if (editingProperty) {
        await updateProperty(editingProperty.id, data);
        toast.success('Property updated!');
      } else {
        await createProperty(data);
        toast.success('Property created!');
      }
      setShowModal(false);
      setEditingProperty(null);
      setForm(emptyForm);
      fetchProperties();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save property');
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    // Prefer the IDR-anchored value (static) when present, else fall back to
    // the legacy USD-stored value. The corresponding `*_currency` flag is
    // flipped to match so the form re-saves correctly without conversion drift.
    const editPriceVal = property.price_idr ?? property.price;
    const editPriceCurrency = property.price_idr ? 'IDR' : (property.price_currency || 'USD');

    const editNightlyVal = property.rental_price_idr ?? property.rental_price;
    const editNightlyCurrency = property.rental_price_idr ? 'IDR' : (property.rental_price_currency || 'USD');

    const editMonthlyVal = property.monthly_rental_price_idr ?? property.monthly_rental_price;
    const editMonthlyCurrency = property.monthly_rental_price_idr ? 'IDR' : 'USD';

    const editYearlyVal = property.yearly_rental_price_idr ?? property.yearly_rental_price;
    const editYearlyCurrency = property.yearly_rental_price_idr ? 'IDR' : 'USD';

    setForm({
      ...property,
      land_size: property.land_size?.toString() || '',
      building_size: property.building_size?.toString() || '',
      price: editPriceVal?.toString() || '',
      price_currency: editPriceCurrency,
      rental_price: editNightlyVal?.toString() || '',
      rental_price_currency: editNightlyCurrency,
      monthly_rental_price: editMonthlyVal?.toString() || '',
      monthly_rental_price_currency: editMonthlyCurrency,
      yearly_rental_price: editYearlyVal?.toString() || '',
      yearly_rental_price_currency: editYearlyCurrency,
      // Short-term rental fields
      max_guests: property.max_guests?.toString() || '',
      min_nights: property.min_nights?.toString() || '1',
      max_nights: property.max_nights?.toString() || '',
      cleaning_fee: property.cleaning_fee?.toString() || '',
      check_in_time: property.check_in_time || '15:00',
      check_out_time: property.check_out_time || '11:00',
      // Leasehold fields
      lease_years_remaining: property.lease_years_remaining?.toString() || '',
      lease_expiry_year: property.lease_expiry_year?.toString() || '',
      lease_extendable: property.lease_extendable || false,
      latitude: property.latitude?.toString() || '',
      longitude: property.longitude?.toString() || '',
      ownership_type: property.ownership_type || '',
      zoning: property.zoning || '',
      road_access: property.road_access || '',
      certificate_of_ownership: property.certificate_of_ownership || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteProperty(id);
      toast.success('Property deleted');
      setDeleteConfirm(null);
      fetchProperties();
    } catch (err) {
      toast.error('Failed to delete property');
    }
  };

  const statusColors = {
    for_sale: 'bg-ubud-jungle',
    for_rent: 'bg-temple-gold',
    for_rent_long: 'bg-blue-500',
  };

  // Search + Filter + Sort
  const filteredProperties = useMemo(() => {
    let list = properties;

    if (statusFilter !== 'all') {
      list = list.filter((p) => p.status === statusFilter);
    }

    if (debouncedSearch) {
      const q = debouncedSearch;
      list = list.filter((p) => {
        return (
          (p.listing_code || '').toLowerCase().includes(q) ||
          (p.name || '').toLowerCase().includes(q) ||
          (p.display_name || '').toLowerCase().includes(q) ||
          (p.location || '').toLowerCase().includes(q) ||
          (p.property_type || '').toLowerCase().includes(q)
        );
      });
    }

    const sorted = [...list];
    switch (sortBy) {
      case 'oldest':
        sorted.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
        break;
      case 'price_asc':
        sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price_desc':
        sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'name_asc':
        sorted.sort((a, b) => (a.display_name || a.name || '').localeCompare(b.display_name || b.name || ''));
        break;
      case 'name_desc':
        sorted.sort((a, b) => (b.display_name || b.name || '').localeCompare(a.display_name || a.name || ''));
        break;
      case 'code':
        sorted.sort((a, b) => (a.listing_code || '').localeCompare(b.listing_code || ''));
        break;
      case 'newest':
      default:
        sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    }
    return sorted;
  }, [properties, debouncedSearch, statusFilter, sortBy]);

  return (
    <div data-testid="admin-properties">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h1 className="font-heading text-2xl font-semibold text-charcoal">Properties</h1>
        <Button
          onClick={() => {
            setEditingProperty(null);
            setForm(emptyForm);
            setShowModal(true);
          }}
          className="bg-ubud-jungle hover:bg-ubud-jungle-dark text-white"
          data-testid="add-property-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Property
        </Button>
      </div>

      {/* Search + Filter + Sort toolbar */}
      <div className="bg-white rounded-xl shadow-sm p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-muted" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by code, name, location, type…"
            className="pl-9"
            data-testid="properties-search-input"
          />
        </div>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="properties-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="for_sale">For Sale</SelectItem>
            <SelectItem value="for_rent_short">Managed by Lalita</SelectItem>
            <SelectItem value="for_rent_long">Long-term Rental</SelectItem>
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-52" data-testid="properties-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Sort: Newest first</SelectItem>
            <SelectItem value="oldest">Sort: Oldest first</SelectItem>
            <SelectItem value="name_asc">Sort: Name (A → Z)</SelectItem>
            <SelectItem value="name_desc">Sort: Name (Z → A)</SelectItem>
            <SelectItem value="code">Sort: Listing code</SelectItem>
            <SelectItem value="price_asc">Sort: Price (low → high)</SelectItem>
            <SelectItem value="price_desc">Sort: Price (high → low)</SelectItem>
          </SelectContent>
        </Select>

        <div className="text-sm text-charcoal-muted ml-auto" data-testid="properties-result-count">
          {loading ? 'Loading…' : `${filteredProperties.length} of ${properties.length}`}
        </div>
      </div>

      {/* Properties Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Property</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Sale Price</TableHead>
              <TableHead>Price/Night</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">Loading...</TableCell>
              </TableRow>
            ) : filteredProperties.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  {properties.length === 0 ? 'No properties yet' : 'No properties match your search'}
                </TableCell>
              </TableRow>
            ) : (
              filteredProperties.map((property) => (
                <TableRow key={property.id}>
                  <TableCell>
                    <span className="font-mono text-xs bg-warm-sand px-2 py-1 rounded">
                      {property.listing_code || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <img
                        src={property.cover_image || property.images?.[0] || 'https://via.placeholder.com/60'}
                        alt={property.display_name || property.name}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                      <div>
                        <div className="font-medium text-charcoal">{property.display_name || property.name}</div>
                        <div className="text-charcoal-muted text-xs italic">{property.name}</div>
                        <div className="text-charcoal-muted text-xs">{property.bedrooms} bed · {property.bathrooms} bath</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{property.location}</TableCell>
                  <TableCell className="capitalize">{property.property_type?.replace('_', ' ')}</TableCell>
                  <TableCell>
                    {property.price > 0 ? (
                      <div className="text-sm">
                        <div className="font-medium">${property.price?.toLocaleString()}</div>
                        <div className="text-charcoal-muted text-xs">
                          IDR {Math.round(property.price * currencyRate).toLocaleString()}
                        </div>
                      </div>
                    ) : (
                      <span className="text-charcoal-muted text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {property.rental_price ? (
                      <div className="text-sm">
                        <div className="font-medium">${property.rental_price?.toLocaleString()}</div>
                        <div className="text-charcoal-muted text-xs">
                          IDR {Math.round(property.rental_price * currencyRate).toLocaleString()}
                        </div>
                      </div>
                    ) : (
                      <span className="text-charcoal-muted text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`${statusColors[property.status]} text-white text-xs px-2 py-1 rounded-full`}>
                      {property.status?.replace('_', ' ')}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(property)}
                        data-testid={`edit-property-${property.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirm(property.id)}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-property-${property.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PropertyFormModal
        showModal={showModal}
        setShowModal={setShowModal}
        editingProperty={editingProperty}
        form={form}
        setForm={setForm}
        locations={locations}
        currencyRate={currencyRate}
        cloudinaryEnabled={cloudinaryEnabled}
        uploading={uploading}
        uploadProgress={uploadProgress}
        uploadQueue={uploadQueue}
        preparing={preparing}
        onPickStart={() => setPreparing(true)}
        handleImageUpload={handleImageUpload}
        handleSubmit={handleSubmit}
        removeImage={removeImage}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Property?</DialogTitle>
          </DialogHeader>
          <p className="text-charcoal-light">This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button
              onClick={() => handleDelete(deleteConfirm)}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PropertiesManagement;
